import {
  LogOut,
  Target,
  ChevronDown,
  Sparkles,
  Users,
  ShieldCheck,
  CreditCard,
  Receipt,
  Mail,
  Phone,
  Code2,
} from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navLinkClasses = ({ isActive }) =>
  `text-xs uppercase tracking-widestx font-semibold whitespace-nowrap transition-colors ${
    isActive
      ? "text-ink-900 border-b-[1.5px] border-ink-900 pb-0.5"
      : "text-ink-500 hover:text-ink-900"
  }`;

export default function Header() {
  const { user, logout } = useAuth();
  const initial = user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "?";

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-ink-100 h-16 flex items-center"
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
        {/* logo */}
        <Link to="/dashboard" className="flex items-center gap-3 shrink-0">
          <img
            src="/logo-128.png"
            alt="LeadIQ Pro"
            width="40"
            height="40"
            className="w-10 h-10 shrink-0"
            data-testid="header-logo"
          />
          <div className="leading-tight">
            <div className="font-display font-extrabold text-base tracking-tight text-ink-900">
              LeadIQ Pro
            </div>
            <div className="text-[10px] uppercase tracking-widestx text-ink-400 font-mono">
              Lead Enrichment Engine
            </div>
          </div>
        </Link>

        {user && user !== false && (
          <div className="flex items-center gap-4">
            {/* primary nav — icon-free for a clean type row */}
            <nav className="hidden md:flex items-center gap-5">
              <NavLink
                to="/dashboard"
                data-testid="header-dashboard-link"
                className={navLinkClasses}
                end
              >
                Leads
              </NavLink>
              <NavLink
                to="/settings/outreach"
                data-testid="header-outreach-nav"
                className={navLinkClasses}
              >
                AI Outreach
              </NavLink>
              <NavLink
                to="/find-mobile"
                data-testid="header-find-mobile-nav"
                className={navLinkClasses}
              >
                Find Mobile
              </NavLink>
              <NavLink
                to="/find-email"
                data-testid="header-find-email-nav"
                className={navLinkClasses}
              >
                Find Email
              </NavLink>
              <NavLink
                to="/pricing"
                data-testid="header-pricing-link"
                className={navLinkClasses}
                end
              >
                Pricing
              </NavLink>
            </nav>

            {/* tier badge — compact single-line pill */}
            {user.tier_model && (
              <div
                data-testid="current-tier-badge"
                className={`hidden lg:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widestx font-mono whitespace-nowrap px-2.5 h-8 rounded-sm border ${
                  user.is_trial
                    ? "bg-[#E8F5E9] text-[#2E7D32] border-[#C8E6C9]"
                    : "bg-ink-50 text-ink-700 border-ink-100"
                }`}
                title={`${user.tier} · ${user.tier_model}${user.is_trial && user.trial_days_left != null ? ` · ${user.trial_days_left}d trial` : ""}`}
              >
                <span className="font-bold">{user.tier || "free"}</span>
                <span className="opacity-30">/</span>
                <span className="opacity-70 normal-case tracking-normal">{user.tier_model}</span>
                {user.is_trial && user.trial_days_left != null && (
                  <>
                    <span className="opacity-30">·</span>
                    <span className="font-bold">{user.trial_days_left}d</span>
                  </>
                )}
              </div>
            )}

            {/* user avatar dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="header-user-menu"
                  className="inline-flex items-center gap-2 rounded-sm pl-1 pr-2 h-10 hover:bg-ink-100 transition-colors group"
                >
                  <div className="w-8 h-8 grid place-items-center rounded-sm bg-ink-900 text-white text-xs font-mono font-bold">
                    {initial}
                  </div>
                  <div className="hidden sm:flex flex-col items-start leading-tight max-w-[140px]">
                    <span className="text-xs font-semibold text-ink-900 truncate w-full">
                      {user.name || user.email}
                    </span>
                    {user.name && (
                      <span className="text-[10px] font-mono text-ink-400 truncate w-full">
                        {user.email}
                      </span>
                    )}
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 text-ink-400 group-hover:text-ink-900 transition-colors" />
                </button>
              </DropdownMenuTrigger>

              <DropdownMenuContent
                align="end"
                className="rounded-sm w-64 p-1.5"
                data-testid="header-user-menu-content"
              >
                <DropdownMenuLabel className="px-2 py-1.5">
                  <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                    Signed in as
                  </div>
                  <div className="text-xs font-mono text-ink-900 truncate">
                    {user.email}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                <DropdownMenuItem asChild>
                  <NavLink
                    to="/settings/profile"
                    data-testid="header-profile-link"
                    className="cursor-pointer flex items-center gap-2.5 px-2 py-2 rounded-sm text-sm"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-ink-400" />
                    <div className="flex-1">
                      <div className="text-ink-900 font-medium">ICP Profile</div>
                      <div className="text-[10px] text-ink-400 leading-tight">
                        AI fit-scoring setup
                      </div>
                    </div>
                  </NavLink>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <NavLink
                    to="/settings/team"
                    data-testid="header-team-link"
                    className="cursor-pointer flex items-center gap-2.5 px-2 py-2 rounded-sm text-sm"
                  >
                    <Users className="w-3.5 h-3.5 text-ink-400" />
                    <div className="flex-1">
                      <div className="text-ink-900 font-medium">Team & Seats</div>
                      <div className="text-[10px] text-ink-400 leading-tight">
                        Add teammates · $5/seat
                      </div>
                    </div>
                  </NavLink>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <NavLink
                    to="/settings/outreach"
                    data-testid="header-outreach-link"
                    className="cursor-pointer flex items-center gap-2.5 px-2 py-2 rounded-sm text-sm md:hidden"
                  >
                    <Mail className="w-3.5 h-3.5 text-ink-400" />
                    <div className="flex-1">
                      <div className="text-ink-900 font-medium">AI Outreach</div>
                      <div className="text-[10px] text-ink-400 leading-tight">
                        Connect SMTP · buy email credits
                      </div>
                    </div>
                  </NavLink>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <NavLink
                    to="/settings/mobile"
                    data-testid="header-mobile-link"
                    className="cursor-pointer flex items-center gap-2.5 px-2 py-2 rounded-sm text-sm"
                  >
                    <Phone className="w-3.5 h-3.5 text-ink-400" />
                    <div className="flex-1">
                      <div className="text-ink-900 font-medium">Find Mobile</div>
                      <div className="text-[10px] text-ink-400 leading-tight">
                        Buy credits · reveal phone numbers
                      </div>
                    </div>
                  </NavLink>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <NavLink
                    to="/settings/billing"
                    data-testid="header-billing-link"
                    className="cursor-pointer flex items-center gap-2.5 px-2 py-2 rounded-sm text-sm"
                  >
                    <Receipt className="w-3.5 h-3.5 text-ink-400" />
                    <div className="flex-1">
                      <div className="text-ink-900 font-medium">Billing & invoices</div>
                      <div className="text-[10px] text-ink-400 leading-tight">
                        Download PDF receipts
                      </div>
                    </div>
                  </NavLink>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <NavLink
                    to="/settings/api"
                    data-testid="header-api-link"
                    className="cursor-pointer flex items-center gap-2.5 px-2 py-2 rounded-sm text-sm"
                  >
                    <Code2 className="w-3.5 h-3.5 text-ink-400" />
                    <div className="flex-1">
                      <div className="text-ink-900 font-medium">Developer API</div>
                      <div className="text-[10px] text-ink-400 leading-tight">
                        Keys · request log · webhooks
                      </div>
                    </div>
                  </NavLink>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <NavLink
                    to="/pricing"
                    data-testid="header-pricing-menu"
                    className="cursor-pointer flex items-center gap-2.5 px-2 py-2 rounded-sm text-sm md:hidden"
                  >
                    <CreditCard className="w-3.5 h-3.5 text-ink-400" />
                    <div className="text-ink-900 font-medium">Pricing & plans</div>
                  </NavLink>
                </DropdownMenuItem>

                {(user.role === "super_admin" || user.role === "admin") && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <NavLink
                        to="/admin"
                        data-testid="header-admin-link"
                        className="cursor-pointer flex items-center gap-2.5 px-2 py-2 rounded-sm text-sm"
                      >
                        <ShieldCheck className="w-3.5 h-3.5 text-[#F57F17]" />
                        <div className="flex-1">
                          <div className="text-ink-900 font-medium">Admin</div>
                          <div className="text-[10px] text-ink-400 leading-tight">
                            Manage users & trials
                          </div>
                        </div>
                      </NavLink>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <NavLink
                        to="/admin/affiliates"
                        data-testid="header-admin-affiliates-link"
                        className="cursor-pointer flex items-center gap-2.5 px-2 py-2 rounded-sm text-sm"
                      >
                        <ShieldCheck className="w-3.5 h-3.5 text-[#F57F17]" />
                        <div className="flex-1">
                          <div className="text-ink-900 font-medium">Affiliate partners</div>
                          <div className="text-[10px] text-ink-400 leading-tight">
                            Manage partners & discounts
                          </div>
                        </div>
                      </NavLink>
                    </DropdownMenuItem>
                  </>
                )}

                {(user.role === "partner" || user.role === "super_admin") && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <NavLink
                        to="/partner"
                        data-testid="header-partner-link"
                        className="cursor-pointer flex items-center gap-2.5 px-2 py-2 rounded-sm text-sm"
                      >
                        <ShieldCheck className="w-3.5 h-3.5 text-brand-600" />
                        <div className="flex-1">
                          <div className="text-ink-900 font-medium">Partner dashboard</div>
                          <div className="text-[10px] text-ink-400 leading-tight">
                            Your link, clicks & revenue
                          </div>
                        </div>
                      </NavLink>
                    </DropdownMenuItem>
                  </>
                )}

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={logout}
                  className="cursor-pointer flex items-center gap-2.5 px-2 py-2 rounded-sm text-sm text-[#D92D20] focus:text-[#9F1F17] focus:bg-[#FEF2F2]"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  <span className="font-medium">Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </header>
  );
}
